import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { formatErrorMessage, normalizeApiError } from '../../../utils/apiErrors';
import './tokens.css';

/**
 * Shared Loading / Error / Empty placeholders for the DashboardKit pages.
 *
 * Styling lives in `tokens.css` (`.dk-state*`), so these also work outside a
 * MUI theme. Each accepts an optional `sx` for spacing tweaks at the call site.
 */

/**
 * Display string for any rejection.
 *
 * The api modules (effortsApi / fieldSalesApi / sfeApi) and the shared Axios
 * pipeline attach an `apiError` descriptor to every rejection; anything else is
 * normalized here. `formatErrorMessage` only reads the allow-listed message and
 * the reference ID, so raw backend detail never reaches the UI.
 *
 * @param {unknown} error
 * @returns {string}
 */
export function describeDashboardError(error) {
  if (error == null) return '';
  const descriptor =
    typeof error === 'object' && error.apiError ? error.apiError : normalizeApiError(error);
  return formatErrorMessage(descriptor);
}

/**
 * Inline loading indicator.
 * @param {{ label?: string, sx?: object }} props
 */
export function LoadingState({ label = 'Loading…', sx }) {
  return (
    <Box className="dk-state dk-state--loading" role="status" aria-live="polite" sx={sx}>
      <CircularProgress size={20} aria-hidden="true" />
      <Typography component="span" className="dk-state__text">
        {label}
      </Typography>
    </Box>
  );
}

/**
 * Error banner. Shows `title` plus the sanitized message derived from `error`
 * (and its support reference, when the backend sent one).
 * @param {{ title?: string, error?: unknown, onRetry?: () => void, sx?: object }} props
 */
export function ErrorState({ title = 'Something went wrong.', error, onRetry, sx }) {
  const detail = describeDashboardError(error);
  return (
    <Box className="dk-state dk-state--error" role="alert" sx={sx}>
      <span className="dk-state__icon" aria-hidden="true">
        ⚠
      </span>
      <Box className="dk-state__body">
        <Typography component="p" className="dk-state__title">
          {title}
        </Typography>
        {detail ? (
          <Typography component="p" className="dk-state__text">
            {detail}
          </Typography>
        ) : null}
      </Box>
      {onRetry ? (
        <Button size="small" variant="outlined" color="error" onClick={onRetry}>
          Retry
        </Button>
      ) : null}
    </Box>
  );
}

/**
 * Empty-result placeholder.
 * @param {{ title?: string, message?: string, sx?: object }} props
 */
export function EmptyState({ title = 'No data', message = 'No records match the selected filters.', sx }) {
  return (
    <Box className="dk-state dk-state--empty" role="status" sx={sx}>
      <Box className="dk-state__body">
        <Typography component="p" className="dk-state__title">
          {title}
        </Typography>
        {message ? (
          <Typography component="p" className="dk-state__text">
            {message}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}
